import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Progress,
  Textarea,
} from '@heroui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { KNOWLEDGE_BASE_KEYS } from 'hooks/useKnowledgeBase'
import { ArrowLeft, ArrowRight, Globe, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import DomainURLCollector from './DomainURLCollector'
import URLReviewList from './URLReviewList'

export default function WebScrapingModal({ open, setOpen, onSuccess }) {
  const [currentStep, setCurrentStep] = useState(1)
  const [urls, setUrls] = useState([])
  const [selectedUrls, setSelectedUrls] = useState([])
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  })
  const [showSuccess, setShowSuccess] = useState(false)

  const queryClient = useQueryClient()

  const steps = [
    { id: 1, name: 'Collect', description: 'Add URLs and discover pages' },
    { id: 2, name: 'Review', description: 'Review selected URLs' },
    { id: 3, name: 'Configure', description: 'Set name and description' },
    { id: 4, name: 'Process', description: 'Scrape and index content' },
  ]

  const resetModal = () => {
    setCurrentStep(1)
    setUrls([])
    setSelectedUrls([])
    setFormData({ name: '', description: '' })
    setShowSuccess(false)
    setOpen(false)
  }

  // No longer needed - sitemap fetching is handled per-domain in DomainURLCollector

  // Scrape URLs mutation
  const { mutate: scrapeUrls, isPending: isScraping } = useMutation({
    mutationFn: (data) => {
      return $fetch('/v1/knowledge/learn', {
        method: 'POST',
        body: data,
      })
    },
    onSuccess: () => {
      toast.success('Scraping started! Processing in background.')

      // Show success state after minimum 2 seconds
      setTimeout(() => {
        setShowSuccess(true)

        // Invalidate knowledge base queries to show new KB immediately
        queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASE_KEYS.lists() })

        onSuccess()
      }, 2000)
    },
    onError: (error) => {
      console.error('Scraping error:', error)
      const errorMessage = error.data?.errors?.[0]?.message || 'Failed to start scraping'
      toast.error(errorMessage)
    },
  })

  const handleNext = () => {
    if (currentStep === 1) {
      if (selectedUrls.length === 0) {
        toast.error('Please add at least one URL to continue')
        return
      }
      setCurrentStep(2)
    }
    else if (currentStep === 2) {
      if (selectedUrls.length === 0) {
        toast.error('Please select at least one URL to scrape')
        return
      }
      setCurrentStep(3)
    }
    else if (currentStep === 3) {
      setCurrentStep(4)

      // Start scraping
      scrapeUrls({
        type: 'web',
        urls: selectedUrls,
        name: formData.name || 'Web Scraped Content',
        description: formData.description || `Scraped content from ${selectedUrls.length} pages`,
      })
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const getStepContent = () => {
    // Helper function to get unique domains count
    const getUniqueDomainsCount = () => {
      const domains = new Set()
      selectedUrls.forEach((url) => {
        try {
          domains.add(new URL(url).hostname)
        }
        catch {
          // Invalid URL, skip
        }
      })
      return domains.size
    }

    switch (currentStep) {
      case 1:
        return (
          <DomainURLCollector
            urls={urls}
            onUrlsChange={setUrls}
            selectedUrls={selectedUrls}
            onSelectedUrlsChange={setSelectedUrls}
          />
        )

      case 2:
        return (
          <URLReviewList
            selectedUrls={selectedUrls}
            onSelectedUrlsChange={setSelectedUrls}
          />
        )

      case 3:
        return (
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium">
                Knowledge Base Name
                <span className="ml-1 text-gray-400">(Optional)</span>
              </label>
              <Input
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Company Documentation"
                radius="none"
                classNames={{
                  input: 'bg-gray-50',
                  inputWrapper: 'border-gray-200',
                }}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Description</label>
              <Textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this content is about..."
                minRows={3}
                radius="none"
                classNames={{
                  input: 'bg-gray-50',
                  inputWrapper: 'border-gray-200',
                }}
              />
            </div>

            {/* Scraping Overview */}
            <div className="border border-gray-200 bg-white p-4">
              <h4 className="mb-3 font-semibold text-gray-900">Scraping Overview</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-blue-600"></div>
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">{selectedUrls.length}</span>
                    {' '}
                    page
                    {selectedUrls.length !== 1 ? 's' : ''}
                    {' '}
                    selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-purple-600"></div>
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">
                      {getUniqueDomainsCount()}
                    </span>
                    {' '}
                    unique domain
                    {getUniqueDomainsCount() !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-green-600"></div>
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">
                      2
                      {' '}
                      -
                      {' '}
                      4
                      {' '}
                      min
                    </span>
                    {' '}
                    estimated
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                  <span className="text-sm text-gray-700">
                    Runs in
                    {' '}
                    <span className="font-medium">background</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )

      case 4:
        return (
          <div className="space-y-6 text-center">
            {!showSuccess
              ? (
                  <>
                    <div className="flex justify-center">
                      <Loader2 className="h-16 w-16 animate-spin text-blue-600" />
                    </div>
                    <div>
                      <h3 className="mb-2 text-lg font-medium">Starting Scraping Process</h3>
                      <p className="text-gray-600">
                        Setting up scraping for
                        {' '}
                        {selectedUrls.length}
                        {' '}
                        pages... Please wait.
                      </p>
                    </div>
                  </>
                )
              : (
                  <>
                    <div className="flex justify-center">
                      <div className={`
                        flex h-16 w-16 items-center justify-center rounded-full
                        bg-green-100
                      `}
                      >
                        <div className="h-8 w-8 rounded-full bg-green-600"></div>
                      </div>
                    </div>
                    <div>
                      <h3 className="mb-2 text-lg font-medium">Scraping Started Successfully!</h3>
                      <p className="mb-4 text-gray-600">
                        Your content is being scraped and indexed in the background.
                        <br />
                        This process will take approximately 2-4 minutes to complete.
                      </p>
                      <p className="text-sm text-gray-500">
                        You can close this modal and continue using the application.
                        <br />
                        You'll be notified when the indexing is complete.
                      </p>
                    </div>
                  </>
                )}
          </div>
        )

      default:
        return null
    }
  }

  const getCurrentStepIndex = () => steps.findIndex(step => step.id === currentStep)

  return (
    <Modal
      isOpen={open}
      onClose={resetModal}
      size="4xl"
      scrollBehavior="inside"
      radius="none"
      classNames={{
        base: 'max-h-[95vh] max-w-6xl',
        body: 'p-0',
        wrapper: 'items-start mt-8',
      }}
    >
      <ModalContent radius="none">
        <ModalHeader className="border-b bg-gray-50">
          <div className="w-full">
            <div className="mb-4 flex items-center gap-3">
              <Globe className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-semibold">Web Scraping Wizard</h2>
            </div>

            {/* Progress Steps */}
            <div className="mb-2 flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`
                    flex h-8 w-8 items-center justify-center text-sm font-medium
                    ${
                step.id <= currentStep
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-600'
                }
                  `}
                  >
                    {step.id < currentStep ? '✓' : step.id}
                  </div>
                  <div className={`
                    ml-2 hidden
                    sm:block
                  `}
                  >
                    <div className="text-sm font-medium">{step.name}</div>
                    <div className="text-xs text-gray-500">{step.description}</div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`
                      ml-4 h-0.5 w-12
                      ${
                    step.id < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                    }
                    `}
                    />
                  )}
                </div>
              ))}
            </div>

            <Progress
              value={(getCurrentStepIndex() + 1) / steps.length * 100}
              className="w-full"
              color="primary"
              size="sm"
            />
          </div>
        </ModalHeader>

        <ModalBody className="p-6">
          {getStepContent()}
        </ModalBody>

        <ModalFooter className="border-t">
          <div className="flex w-full justify-between">
            <Button
              variant="bordered"
              onPress={handleBack}
              isDisabled={currentStep === 1 || currentStep === 4 || isScraping}
              radius="none"
              className="border-gray-200"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>

            <div className="flex gap-2">
              {currentStep === 4 && showSuccess
                ? (
                    <Button
                      color="primary"
                      onPress={resetModal}
                      radius="none"
                    >
                      Close
                    </Button>
                  )
                : (
                    <>
                      <Button
                        variant="bordered"
                        onPress={resetModal}
                        radius="none"
                        className="border-gray-200"
                        isDisabled={isScraping}
                      >
                        {currentStep === 4 && showSuccess ? 'Close' : 'Cancel'}
                      </Button>

                      {currentStep < 4
                        ? (
                            <Button
                              color="primary"
                              onPress={handleNext}
                              isLoading={isScraping}
                              isDisabled={currentStep === 1 && selectedUrls.length === 0}
                              radius="none"
                            >
                              {currentStep === 3 ? 'Start Scraping' : 'Next'}
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          )
                        : (
                            currentStep === 4 && !showSuccess && (
                              <Button
                                color="primary"
                                isLoading={true}
                                isDisabled={true}
                                radius="none"
                              >
                                Processing...
                              </Button>
                            )
                          )}
                    </>
                  )}
            </div>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
